import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Popover } from 'antd';
import IconFont from '$/components/IconFont';
import Tooltip from '$/components/Tooltip';
// import ModelSelectPopup from '$/components/ModelSelectPopup';
// import SeedanceAgreementModal from '$/components/SeedanceAgreementModal';
import { useLanguageStore } from '$/store/languageStore';
import { mtstatTrack } from '$/utils/mtstat';
// import { isSeedanceV2ModelValue, hasSeedanceV2Agreement } from '$/utils/seedanceAgreement';
import { InputPanelToolBarProps, UploadButtonItem } from './types';
import SkillSelectPopup from './SkillSelectPopup';
import styles from './index.module.scss';

/** 渲染输入面板底部工具栏，并通过外部注入承载业务资产库实现。 */
function InputPanelToolBar({
  disabled = false,
  // Upload
  uploadButtonList,
  onUploadLimitToast,
  uploadPopupContent,
  uploadPopoverOpen,
  onUploadPopoverOpenChange,
  // Model
  showModelSelect,
  modelMap,
  usedModel,
  setUsedModel,
  modelSelectContent,
  // Skill
  showSkillSelect,
  skillCategories,
  skillList,
  selectedSkill,
  onSkillSelect,
  onSkillShow,
  onSkillClick,
  onSkillTabClick,
  skillPopupTitle,
  skillSelectContent,
  // Submit
  isSubmitDisabled,
  onSubmit,
  renderSubmitButton,
  // Asset library
  assetLibraryVisible,
  onCloseAssetLibrary,
  renderAssetModalContent,
  onAssetItemClick,
  onAssetBatchConfirm,
  trackLocation = 'home_page',
}: InputPanelToolBarProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);

  // ─── Upload Popover ─────────────────────────────────────────────────────
  const [internalPopoverOpen, setInternalPopoverOpen] = useState(false);
  const popoverOpen = uploadPopoverOpen ?? internalPopoverOpen;
  const handlePopoverOpenChange = onUploadPopoverOpenChange ?? setInternalPopoverOpen;

  // ─── Skill Popover ──────────────────────────────────────────────────────
  const [skillPopoverOpen, setSkillPopoverOpen] = useState(false);

  /** 工具栏禁用时收起已打开浮层，避免发送中或加载中仍能操作上传/Skill。 */
  useEffect(() => {
    if (!disabled) return;
    handlePopoverOpenChange(false);
    setSkillPopoverOpen(false);
  }, [disabled, handlePopoverOpenChange]);

  // ─── Seedance 协议弹窗 ──────────────────────────────────────────────────
  // const [seedanceAgreementOpen, setSeedanceAgreementOpen] = useState(false);
  // const seedanceAgreementProceedRef = useRef<(() => void) | null>(null);

  // const modelDisplayName = useMemo(() => {
  //   if (!usedModel) return '';
  //   const activeType = usedModel.activeType || 'imageModel';
  //   const modelInfo = (usedModel as any)[activeType];
  //   const usedModelName = modelInfo?.modelName || '';
  //   return usedModelName || languageStore.smartcatTranslationText((v: any) => v.roboneo_auto);
  // }, [usedModel, languageStore]);

  // ─── 内置上传弹框渲染 ──────────────────────────────────────────────────
  function renderUploadPopup() {
    if (uploadPopupContent) return uploadPopupContent;
    if (!uploadButtonList?.length) return null;
    return (
      <div className={styles.uploadButtonList}>
        {uploadButtonList.map((item: UploadButtonItem) => (
          <div
            key={item.key}
            className={classNames(styles.uploadButtonItem, {
              [styles.disabled]: item.disabled,
            })}
            onClick={() => {
              if (item.disabled) {
                if (item.key === 'image' || item.key === 'video' || item.key === 'file') {
                  onUploadLimitToast?.(item.key as 'image' | 'video' | 'file');
                }
                return;
              }
              item.onClick();
              handlePopoverOpenChange(false);
            }}
          >
            <IconFont name={item.icon} fontSize={16} className={styles.icon} />
            <span className={styles.uploadButtonItemText}>{item.text}</span>
          </div>
        ))}
      </div>
    );
  }

  // ─── 内置模型选择弹框渲染 ─────────────────────────────────────────────
  // function renderModelSelectPopup() {
  //   if (modelSelectContent) return modelSelectContent;
  //   if (!modelMap || !usedModel || !setUsedModel) return null;
  //   return (
  //     <ModelSelectPopup
  //       modelMap={modelMap}
  //       usedModel={usedModel}
  //       setUsedModel={setUsedModel}
  //       onBeforeModelChange={(model, proceed) => {
  //         if (isSeedanceV2ModelValue(model.modelValue) && !hasSeedanceV2Agreement()) {
  //           seedanceAgreementProceedRef.current = proceed;
  //           setSeedanceAgreementOpen(true);
  //         } else {
  //           proceed();
  //         }
  //       }}
  //     />
  //   );
  // }

  /** 渲染内置 Skill 选择弹框，并透传业务埋点回调。 */
  function renderSkillSelectPopup() {
    if (skillSelectContent) return skillSelectContent;
    return (
      <SkillSelectPopup
        title={skillPopupTitle ?? languageStore.smartcatTranslationText((v) => v.skills)}
        categories={skillCategories || []}
        skillList={skillList || []}
        selectedSkill={selectedSkill}
        onShow={onSkillShow}
        onSkillClick={onSkillClick}
        onTabClick={onSkillTabClick}
        onSelect={(skill) => {
          onSkillSelect?.(skill);
          setSkillPopoverOpen(false);
        }}
      />
    );
  }

  return (
    <>
      <div className={styles.inputToolBar}>
        <div className={styles.modelSlot}>
          <Tooltip
            title={languageStore.smartcatTranslationText(
              (v) => v.RoboNeo_Web_studio_tool_add,
            )}
            placement="top"
          >
            <Popover
              placement="topLeft"
              open={disabled ? false : popoverOpen}
              onOpenChange={(open) => {
                if (disabled) return;
                handlePopoverOpenChange(open);
              }}
              overlayInnerStyle={{ padding: '0px' }}
              overlayStyle={{ marginTop: 5 }}
              content={renderUploadPopup()}
              trigger={disabled ? [] : 'click'}
              overlayClassName="resetPopoverAndArrow"
            >
              <div className={classNames(styles.upload, { [styles.disabled]: disabled })}>
                <IconFont name="plusBold" fontSize={14} className={styles.icon} />
              </div>
            </Popover>
          </Tooltip>

          {/* {showModelSelect ? (
            <Tooltip
              title={languageStore.smartcatTranslationText(
                (v) => v.roboneo_model_change,
              )}
              placement="top"
            >
              <Popover
                placement="topRight"
                overlayInnerStyle={{ padding: '0px' }}
                overlayStyle={{ padding: 0, paddingBottom: '4px' }}
                content={renderModelSelectPopup()}
                trigger="click"
                overlayClassName="resetPopoverAndArrow"
              >
                <div
                  className={classNames(styles.modelButton, {
                    [styles.padding]: true,
                  })}
                  onClick={() => {
                    mtstatTrack('roboneo_input_box_click', { icon_type: 'model' });
                  }}
                >
                  <IconFont name="aiModelBold" fontSize={14} className={styles.icon} />
                  <span className={styles.modelText}>{modelDisplayName}</span>
                </div>
              </Popover>
            </Tooltip>
          ) : null} */}

          {showSkillSelect ? (
            <Popover
              placement="topLeft"
              open={disabled ? false : skillPopoverOpen}
              onOpenChange={(open) => {
                if (disabled) return;
                setSkillPopoverOpen(open);
              }}
              overlayInnerStyle={{ padding: '0px' }}
              overlayStyle={{ padding: 0, paddingBottom: '4px' }}
              content={renderSkillSelectPopup()}
              trigger={disabled ? [] : 'click'}
              overlayClassName="resetPopoverAndArrow"
            >
              <Tooltip
                title={languageStore.smartcatTranslationText(
                  (v) => v.skills,
                )}
                placement="top"
              >
                <div
                  className={classNames(styles.skillButton, {
                    [styles.active]: !!selectedSkill,
                    [styles.disabled]: disabled,
                  })}
                  onClick={() => {
                    if (disabled) return;
                    mtstatTrack('roboneo_input_box_click', { icon_type: 'skill' });
                  }}
                >
                  <IconFont name="bookBold" fontSize={14} className={styles.icon} />
                </div>
              </Tooltip>
            </Popover>
          ) : null}
        </div>

        {renderSubmitButton ? renderSubmitButton() : (
          <div
            className={classNames(styles.submitButton, { [styles.disabled]: isSubmitDisabled })}
            onClick={onSubmit}
            aria-label="提交"
          >
            <IconFont name="arrowUpBlack" fontSize={16} color="currentColor" />
          </div>
        )}
      </div>

      {/* <SeedanceAgreementModal
        open={seedanceAgreementOpen}
        onClose={() => setSeedanceAgreementOpen(false)}
        onAgree={() => {
          setSeedanceV2Agreement();
          setSeedanceAgreementOpen(false);
          seedanceAgreementProceedRef.current?.();
          seedanceAgreementProceedRef.current = null;
        }}
      /> */}

      {assetLibraryVisible && renderAssetModalContent && createPortal(
        <div className={styles.assetLibraryOverlay} role="presentation">
          <button
            type="button"
            className={styles.assetLibraryBackdrop}
            aria-label="关闭资产库"
            onClick={onCloseAssetLibrary}
          />

          {renderAssetModalContent({
            setOpenAssetPopover: () => { onCloseAssetLibrary?.(); },
            trackLocation,
            onAssetItemClick: (item) => { onAssetItemClick?.(item); },
            onAssetBatchConfirm: (items) => { onAssetBatchConfirm?.(items); },
            enableAddToAssetLibrary: true,
          })}
        </div>,
        document.body,
      )}
    </>
  );
}

export default React.memo(InputPanelToolBar);
